import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { CompromisoConContexto } from '@/types';

export type EstadoFiltro = 'pendientes' | 'bloqueados' | 'completados' | 'todos';

export interface FiltrosCompromisos {
  estado: EstadoFiltro;
  plantas: string[]; // facility slugs
  propuestas: string[]; // propuesta ids
  iniciativas: string[]; // iniciativa ids
  evs: string[]; // ev ids
  tags: string[]; // tag ids
  search: string;
}

const ESTADOS_VALIDOS: EstadoFiltro[] = ['pendientes', 'bloqueados', 'completados', 'todos'];

function parseList(s: string | null): string[] {
  if (!s) return [];
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}

function stringifyList(arr: string[]): string | null {
  return arr.length ? arr.join(',') : null;
}

/**
 * Hook que maneja el estado de filtros sincronizado con query params.
 *
 * URL: `?estado=pendientes&plantas=lampa,quilpue&propuestas=p1,p2&tags=t1`
 *
 * Cuando se actualiza un filtro padre (plantas), los filtros hijos
 * (propuestas, iniciativas, evs) se podan automáticamente para sacar
 * los valores que ya no son válidos según la jerarquía visible.
 */
export function useFiltrosCompromisos(compromisos: CompromisoConContexto[]) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filtros: FiltrosCompromisos = useMemo(() => {
    const estadoRaw = searchParams.get('estado') as EstadoFiltro | null;
    return {
      estado: estadoRaw && ESTADOS_VALIDOS.includes(estadoRaw) ? estadoRaw : 'pendientes',
      plantas: parseList(searchParams.get('plantas')),
      propuestas: parseList(searchParams.get('propuestas')),
      iniciativas: parseList(searchParams.get('iniciativas')),
      evs: parseList(searchParams.get('evs')),
      tags: parseList(searchParams.get('tags')),
      search: searchParams.get('q') || '',
    };
  }, [searchParams]);

  const update = useCallback(
    (patch: Partial<FiltrosCompromisos>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const apply = (key: string, val: string | string[] | undefined) => {
            if (val === undefined) return;
            if (typeof val === 'string') {
              if (val) next.set(key, val);
              else next.delete(key);
            } else {
              const s = stringifyList(val);
              if (s) next.set(key, s);
              else next.delete(key);
            }
          };
          if ('estado' in patch) {
            if (patch.estado && patch.estado !== 'pendientes') next.set('estado', patch.estado);
            else next.delete('estado');
          }
          apply('plantas', patch.plantas);
          apply('propuestas', patch.propuestas);
          apply('iniciativas', patch.iniciativas);
          apply('evs', patch.evs);
          apply('tags', patch.tags);
          if ('search' in patch) apply('q', patch.search);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // ── Listas únicas de opciones disponibles (jerárquico) ──────────────────

  // Plantas (facilities) — siempre todas las del cliente
  const opcionesPlantas = useMemo(() => {
    const m = new Map<string, { slug: string; nombre: string }>();
    for (const c of compromisos) {
      if (c.facility) m.set(c.facility.slug, { slug: c.facility.slug, nombre: c.facility.nombre });
    }
    return [...m.values()].sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [compromisos]);

  // Propuestas — solo de las plantas filtradas (si hay filtro de plantas)
  const opcionesPropuestas = useMemo(() => {
    const m = new Map<string, { id: string; nombre: string; facilitySlug: string }>();
    for (const c of compromisos) {
      if (!c.propuesta || !c.facility) continue;
      if (filtros.plantas.length && !filtros.plantas.includes(c.facility.slug)) continue;
      m.set(c.propuesta.id, {
        id: c.propuesta.id,
        nombre: `${c.propuesta.codigo} · ${c.propuesta.nombre}`,
        facilitySlug: c.facility.slug,
      });
    }
    return [...m.values()].sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [compromisos, filtros.plantas]);

  // Iniciativas — filtradas por propuestas si hay
  const opcionesIniciativas = useMemo(() => {
    const m = new Map<string, { id: string; nombre: string; propuestaId: string }>();
    for (const c of compromisos) {
      if (!c.iniciativa || !c.propuesta || !c.facility) continue;
      if (filtros.plantas.length && !filtros.plantas.includes(c.facility.slug)) continue;
      if (filtros.propuestas.length && !filtros.propuestas.includes(c.propuesta.id)) continue;
      m.set(c.iniciativa.id, {
        id: c.iniciativa.id,
        nombre: `${c.iniciativa.codigo} · ${c.iniciativa.nombre}`,
        propuestaId: c.propuesta.id,
      });
    }
    return [...m.values()].sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [compromisos, filtros.plantas, filtros.propuestas]);

  // Entregas de Valor — filtradas por propuestas
  const opcionesEvs = useMemo(() => {
    const m = new Map<string, { id: string; nombre: string; color: string }>();
    for (const c of compromisos) {
      if (!c.ev || !c.propuesta || !c.facility) continue;
      if (filtros.plantas.length && !filtros.plantas.includes(c.facility.slug)) continue;
      if (filtros.propuestas.length && !filtros.propuestas.includes(c.propuesta.id)) continue;
      m.set(c.ev.id, { id: c.ev.id, nombre: `${c.ev.codigo} · ${c.ev.nombre}`, color: c.ev.color });
    }
    return [...m.values()].sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [compromisos, filtros.plantas, filtros.propuestas]);

  // ── Setters con cascada ─────────────────────────────────────────────────

  const setPlantas = useCallback(
    (nuevas: string[]) => {
      // Propuestas que siguen siendo válidas
      const propuestasValidas = new Set<string>();
      const iniciativasValidas = new Set<string>();
      const evsValidos = new Set<string>();
      for (const c of compromisos) {
        if (!c.facility) continue;
        if (nuevas.length && !nuevas.includes(c.facility.slug)) continue;
        if (c.propuesta) propuestasValidas.add(c.propuesta.id);
        if (c.iniciativa) iniciativasValidas.add(c.iniciativa.id);
        if (c.ev) evsValidos.add(c.ev.id);
      }
      update({
        plantas: nuevas,
        propuestas: filtros.propuestas.filter((id) => propuestasValidas.has(id)),
        iniciativas: filtros.iniciativas.filter((id) => iniciativasValidas.has(id)),
        evs: filtros.evs.filter((id) => evsValidos.has(id)),
      });
    },
    [compromisos, filtros.propuestas, filtros.iniciativas, filtros.evs, update],
  );

  const setPropuestas = useCallback(
    (nuevas: string[]) => {
      const iniciativasValidas = new Set<string>();
      const evsValidos = new Set<string>();
      for (const c of compromisos) {
        if (!c.propuesta || !c.facility) continue;
        if (filtros.plantas.length && !filtros.plantas.includes(c.facility.slug)) continue;
        if (nuevas.length && !nuevas.includes(c.propuesta.id)) continue;
        if (c.iniciativa) iniciativasValidas.add(c.iniciativa.id);
        if (c.ev) evsValidos.add(c.ev.id);
      }
      update({
        propuestas: nuevas,
        iniciativas: filtros.iniciativas.filter((id) => iniciativasValidas.has(id)),
        evs: filtros.evs.filter((id) => evsValidos.has(id)),
      });
    },
    [compromisos, filtros.plantas, filtros.iniciativas, filtros.evs, update],
  );

  const setIniciativas = useCallback((nuevas: string[]) => update({ iniciativas: nuevas }), [update]);
  const setEvs = useCallback((nuevas: string[]) => update({ evs: nuevas }), [update]);
  const setTags = useCallback((nuevos: string[]) => update({ tags: nuevos }), [update]);
  const setEstado = useCallback((e: EstadoFiltro) => update({ estado: e }), [update]);
  const setSearch = useCallback((q: string) => update({ search: q }), [update]);

  const limpiarTodo = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Contador de filtros aplicados (excluye estado por default y search)
  const cantidadFiltrosActivos = useMemo(() => {
    let n = 0;
    if (filtros.plantas.length) n += 1;
    if (filtros.propuestas.length) n += 1;
    if (filtros.iniciativas.length) n += 1;
    if (filtros.evs.length) n += 1;
    if (filtros.tags.length) n += 1;
    if (filtros.estado !== 'pendientes') n += 1;
    if (filtros.search) n += 1;
    return n;
  }, [filtros]);

  // ── Aplicación de filtros ───────────────────────────────────────────────

  const compromisosFiltrados = useMemo(() => {
    const term = filtros.search.toLowerCase().trim();
    const list = compromisos.filter((c) => {
      // Estado
      if (filtros.estado === 'pendientes' && !c.flags.isPendiente) return false;
      if (filtros.estado === 'bloqueados' && !c.flags.hasBloqueado) return false;
      if (filtros.estado === 'completados' && !c.flags.isCompletado) return false;
      // Plantas
      if (filtros.plantas.length && (!c.facility || !filtros.plantas.includes(c.facility.slug)))
        return false;
      // Propuestas
      if (filtros.propuestas.length && (!c.propuesta || !filtros.propuestas.includes(c.propuesta.id)))
        return false;
      // Iniciativas
      if (
        filtros.iniciativas.length &&
        (!c.iniciativa || !filtros.iniciativas.includes(c.iniciativa.id))
      )
        return false;
      // EVs
      if (filtros.evs.length && (!c.ev || !filtros.evs.includes(c.ev.id))) return false;
      // Tags (OR — basta que tenga uno de los seleccionados)
      if (filtros.tags.length && !c.tagIds.some((t) => filtros.tags.includes(t))) return false;
      // Búsqueda
      if (!term) return true;
      return (
        c.nombre.toLowerCase().includes(term) ||
        c.codigo.toLowerCase().includes(term) ||
        (c.descripcion || '').toLowerCase().includes(term) ||
        (c.facility?.nombre || '').toLowerCase().includes(term) ||
        (c.propuesta?.nombre || '').toLowerCase().includes(term)
      );
    });
    // Orden jerárquico: planta → propuesta → código del compromiso (natural sort)
    const collator = new Intl.Collator('es', { numeric: true, sensitivity: 'base' });
    return [...list].sort((a, b) => {
      const fA = a.facility?.nombre || '';
      const fB = b.facility?.nombre || '';
      const cmpFac = collator.compare(fA, fB);
      if (cmpFac !== 0) return cmpFac;
      const pA = a.propuesta?.codigo || '';
      const pB = b.propuesta?.codigo || '';
      const cmpProp = collator.compare(pA, pB);
      if (cmpProp !== 0) return cmpProp;
      return collator.compare(a.codigo, b.codigo);
    });
  }, [compromisos, filtros]);

  return {
    filtros,
    setEstado,
    setPlantas,
    setPropuestas,
    setIniciativas,
    setEvs,
    setTags,
    setSearch,
    limpiarTodo,
    cantidadFiltrosActivos,
    opcionesPlantas,
    opcionesPropuestas,
    opcionesIniciativas,
    opcionesEvs,
    compromisosFiltrados,
  };
}
